import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown } from "lucide-react";
import { LucideIcon } from "lucide-react";
import { formatCurrency } from "@/lib/currency";

interface SystemMetricsCardProps {
  title: string;
  value: number | string;
  growth?: number;
  icon: LucideIcon;
  format?: "number" | "currency";
}

export function SystemMetricsCard({
  title,
  value,
  growth,
  icon: Icon,
  format = "number",
}: SystemMetricsCardProps) {
  // Handle string values that shouldn't be converted to numbers
  const isStringValue = typeof value === "string" && isNaN(Number(value));

  // Handle undefined/null values gracefully
  const numericValue =
    !isStringValue && value !== null && value !== undefined ? Number(value) : 0;

  const formattedValue = isStringValue
    ? String(value)
    : format === "currency"
      ? formatCurrency(numericValue, "VND")
      : numericValue.toLocaleString();

  const isPositive = growth !== undefined && growth !== null && growth > 0;
  const isNegative = growth !== undefined && growth !== null && growth < 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{formattedValue}</div>
        {growth !== undefined && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {isPositive && <TrendingUp className="h-3 w-3 text-ok-ink" />}
            {isNegative && (
              <TrendingDown className="h-3 w-3 text-destructive-ink" />
            )}
            <span
              className={
                isPositive ? "text-ok-ink" : isNegative ? "text-destructive-ink" : ""
              }
            >
              {growth > 0 ? "+" : ""}
              {growth}%
            </span>
            <span>from last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
